import React, { useState, useEffect } from 'react';
import {Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Rating,Stack, TextField} from '@mui/material';
import { toast } from 'react-toastify';
import { registrarCsatService, buscarCsatService } from 'services/Csat';

interface ICsatModalProps {
  open: boolean;
  onClose: () => void;
  cpfCnpj: string;
  contrato: string;
  acordoId: string;
  crmId?: string;
  carteiraId?: number;
}

enum CsatTipoPergunta {
  ESCALA_1_5 = 1,
  TEXTO_400 = 2,
  BOOLEANO = 3
}

interface ICsatPergunta {
  id: number;
  texto: string;
  tipo: number;
}

interface ICsatResponse {
  pesquisaId: number;
  perguntas: ICsatPergunta[];
}

const labels: Record<number, string> = {
  1: "Muito insatisfeito",
  2: "Insatisfeito",
  3: "Neutro",
  4: "Satisfeito",
  5: "Muito satisfeito"
};

const ModalCsat: React.FC<ICsatModalProps> = ({
  open,
  onClose,
  cpfCnpj,
  contrato,
  acordoId,
  crmId,
  carteiraId
}) => {

  const [perguntas, setPerguntas] = useState<ICsatPergunta[]>([]);
  const [respostas, setRespostas] = useState<Record<number, string | number | null>>({});
  const [loading, setLoading] = useState(false);
  const [jaRegistrado, setJaRegistrado] = useState(false);

  useEffect(() => {
    if (!open) return;

    const carregarPerguntas = async () => {
      try {
        const data: ICsatResponse = await buscarCsatService();
        setPerguntas(data.perguntas);
      } catch {
        toast.error("Erro ao carregar pesquisa.");
      }
    };

    carregarPerguntas();
  }, [open]);

  const atualizarResposta = (perguntaId: number, valor: string | number | null) => {
    setRespostas(prev => ({
      ...prev,
      [perguntaId]: valor
    }));
  };

  const resetState = () => {
    setRespostas({});
  };

  const enviarCsat = async (mostrarToast = false) => {

    if (jaRegistrado) return;

    const dadosValidos =
      cpfCnpj &&
      contrato &&
      acordoId &&
      crmId &&
      carteiraId != null;

    if (!dadosValidos) return;

    setLoading(true);

    try {

      const payload = {
        cpfCnpj,
        contrato,
        acordoId,
        crmId,
        carteiraId,
        respostas: Object.entries(respostas).map(([perguntaId, valor]) => ({
          perguntaId: Number(perguntaId),
          valor: valor?.toString() ?? null
        }))
      };

      await registrarCsatService(payload);

      sessionStorage.removeItem('@acordoIdCsat');

      setJaRegistrado(true);

      if (mostrarToast) {
        toast.success("Obrigado pelo seu feedback!");
      }

    } catch {
      toast.error("Não foi possível registrar seu feedback.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    await enviarCsat(true);
    resetState();
    onClose();
  };

  const handleSkip = async () => {
    const respostasNulas = perguntas.map(p => ({
      perguntaId: p.id,
      valor: null as string | null
    }));

    const dadosValidos =
      cpfCnpj &&
      contrato &&
      acordoId &&
      crmId &&
      carteiraId != null;

    if (dadosValidos && !jaRegistrado) {
      try {
        await registrarCsatService({
          cpfCnpj,
          contrato,
          acordoId,
          crmId: crmId!,
          carteiraId: carteiraId!,
          respostas: respostasNulas
        });
        sessionStorage.removeItem('@acordoIdCsat');
      } catch {
      }
    }

    resetState();
    onClose();
  };

  const renderPergunta = (pergunta: ICsatPergunta) => {

    if (pergunta.tipo === CsatTipoPergunta.ESCALA_1_5) {

      const valor = respostas[pergunta.id] as number | null;

      return (
        <Box key={pergunta.id} sx={{ background: '#fafafa', borderRadius: 2, p: 3 }}>
          <Typography variant="subtitle1" fontWeight={500} textAlign="center" mb={2}>
            {pergunta.texto}
          </Typography>

          <Box display="flex" justifyContent="center">
            <Rating
              size="large"
              value={valor}
              onChange={(_, value) => atualizarResposta(pergunta.id, value)}
            />
          </Box>

          {valor && labels[valor] && (
            <Typography
              variant="body2"
              textAlign="center"
              color="text.secondary"
              mt={1}
            >
              {labels[valor]}
            </Typography>
          )}
        </Box>
      );
    }

    if (pergunta.tipo === CsatTipoPergunta.TEXTO_400) {
      return (
        <Box key={pergunta.id} sx={{ background: '#fafafa', borderRadius: 2, p: 3 }}>
          <Typography variant="subtitle1" fontWeight={500} textAlign="center" mb={2}>
            {pergunta.texto}
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={3}
            inputProps={{ maxLength: 400 }}
            value={respostas[pergunta.id] ?? ""}
            onChange={(e) =>
              atualizarResposta(pergunta.id, e.target.value)
            }
          />
        </Box>
      );
    }

    return null;
  };

  return (
    <Dialog open={open} onClose={handleSkip} maxWidth="sm" fullWidth>

      <DialogTitle>
        Pesquisa de satisfação
      </DialogTitle>

      <DialogContent>

        <Stack spacing={4} mt={1}>

          <Typography variant="body2" color="text.secondary">
            Sua opinião é muito importante para melhorarmos nossos serviços.
            A pesquisa leva apenas alguns segundos.
          </Typography>

          {perguntas.map(renderPergunta)}

        </Stack>

      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>

        <Button onClick={handleSkip}>
          Pular
        </Button>

        <Button
          variant="contained"
          disabled={loading || jaRegistrado}
          onClick={handleSubmit}
        >
          {loading ? "Enviando..." : "Enviar resposta"}
        </Button>

      </DialogActions>

    </Dialog>
  );
};

export default ModalCsat;